/**
 * ============================================================================
 * MIGRATED FILE — DO NOT EDIT
 * ============================================================================
 *
 * This file has been migrated to academy-content-ui.
 * Canonical source: academy-content-ui/static/js/features/interactiveEducation/hooks/useGenerateAsyncQuizMe.ts
 *
 * This .migrated copy is retained only for LearningCenterUI consumers that
 * have not yet moved to academy-content-ui. All changes should be made in
 * academy-content-ui first, then synced here if still needed.
 *
 * This copy will be deleted when LearningCenterUI is shut down.
 *
 * @migrated-to academy-content-ui
 * ============================================================================
 */
import { isAcceptanceTest } from 'hub-http/helpers/env';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGetAiAccessControls } from 'academy-common-lib/features/aiControl/aiAccessQueries';
import { useAsyncBulkGenerateQuizMeMutation, useAsyncBulkGetQuizMeQuery } from '../../../../__generated__/chirp/com/hubspot/academy/gen/ai/skills/rpc/QuizMeRpc.hooks';
import UsageTracker from '../../../utils/UsageTracker';
import { DIFFICULTY_LEVELS } from '../interactiveEducationConstants.migrated';
import { useAsyncGetLazyPollingQuizMe } from '../queries/quizMeQueries.migrated';
import { collectTaskIdsFromState, normalizeDifficulty, selectPollTaskByTaskId, useQuizMeSession } from '../utils/quizMeUtils.migrated';
import { shuffleTaskAnswers } from '../utils/contentUtils.migrated';
import { useFullShort } from '../../shortContent/shortContentQueries.migrated';
import { handleApolloErrors } from '../../../utils/errorUtils.legacy';
/**
 * @migrated-to academy-content-ui
 * @deprecated
 * This file has been migrated to academy-content-ui.
 * Canonical source: academy-content-ui/static/js/features/interactiveEducation/hooks/useGenerateAsyncQuizMe.ts
 *
 * This .migrated copy is retained only for LearningCenterUI consumers that
 * have not yet moved to academy-content-ui. All changes should be made in
 * academy-content-ui first, then synced here if still needed.
 *
 * This copy will be deleted when LearningCenterUI is shut down.
 */
export const useAsyncGetQuizMeFromCache = ({
  primaryContentId,
  contentType,
  selectedDifficulty,
  skip = false
}) => {
  const shuffledCacheRef = useRef({
    correlationId: undefined,
    result: undefined
  });
  const isShorts = contentType === 'SHORT_FORM_CONTENT';
  const {
    getQuizState
  } = useQuizMeSession();
  const quizState = getQuizState(primaryContentId, contentType);
  const taskIds = useMemo(() => isShorts ? collectTaskIdsFromState(quizState) : [], [isShorts, quizState]);
  const {
    data
  } = useAsyncBulkGetQuizMeQuery({
    variables: {
      taskIds
    },
    fetchPolicy: 'cache-only',
    skip: isAcceptanceTest() || taskIds.length === 0 || skip
  });
  const quizMeData = useMemo(() => {
    var _quizState$difficulty, _task$quizResponse;
    // Falls back to INTERMEDIATE when no difficulty is selected.
    const difficulty = selectedDifficulty !== null && selectedDifficulty !== void 0 ? selectedDifficulty : 'INTERMEDIATE';
    const contentTaskId = quizState === null || quizState === void 0 || (_quizState$difficulty = quizState.difficultyTasks) === null || _quizState$difficulty === void 0 ? void 0 : _quizState$difficulty[difficulty];
    const task = selectPollTaskByTaskId(data === null || data === void 0 ? void 0 : data.tasks, contentTaskId);
    const correlationId = task === null || task === void 0 || (_task$quizResponse = task.quizResponse) === null || _task$quizResponse === void 0 ? void 0 : _task$quizResponse.correlationId;
    const cached = shuffledCacheRef.current;
    if (!correlationId) {
      return task;
    }
    if (correlationId === cached.correlationId) {
      return cached.result;
    }
    const shuffled = shuffleTaskAnswers(task);
    shuffledCacheRef.current = {
      correlationId,
      result: shuffled
    };
    return shuffled;
  }, [data, selectedDifficulty, quizState]);
  return quizMeData;
};
const useGetShortsQuizMeRequestParams = content => {
  const {
    contentPortalId,
    outputLanguage,
    videoId
  } = useMemo(() => ({
    contentPortalId: content !== null && content !== void 0 && content.hubId ? content.hubId : undefined,
    outputLanguage: content === null || content === void 0 ? void 0 : content.language,
    videoId: content !== null && content !== void 0 && content.reference ? Number(content === null || content === void 0 ? void 0 : content.reference) : undefined
  }), [content]);
  return contentPortalId && outputLanguage && videoId ? [{
    contentPortalId,
    outputLanguage,
    videoId
  }] : undefined;
};
/**
 * @migrated-to academy-content-ui
 * @deprecated
 * This file has been migrated to academy-content-ui.
 * Canonical source: academy-content-ui/static/js/features/interactiveEducation/hooks/useGenerateAsyncQuizMe.ts
 *
 * This .migrated copy is retained only for LearningCenterUI consumers that
 * have not yet moved to academy-content-ui. All changes should be made in
 * academy-content-ui first, then synced here if still needed.
 *
 * This copy will be deleted when LearningCenterUI is shut down.
 */
export const useGenerateAsyncQuizMe = ({
  primaryContentId,
  contentType,
  interactiveContentTrackingProps,
  triggerGeneration = true
}) => {
  var _quizState$difficulty2;
  const {
    aiAccessControls
  } = useGetAiAccessControls();
  const isGenerativeAiEnabled = !!(aiAccessControls !== null && aiAccessControls !== void 0 && aiAccessControls.generativeAiEnabled);
  const {
    getQuizState,
    initialiseQuiz,
    deleteQuizState,
    setDifficultyTasks
  } = useQuizMeSession();
  const quizState = getQuizState(primaryContentId, contentType);
  const hasDifficultyTasks = Object.values((_quizState$difficulty2 = quizState === null || quizState === void 0 ? void 0 : quizState.difficultyTasks) !== null && _quizState$difficulty2 !== void 0 ? _quizState$difficulty2 : {}).some(Boolean);
  const generationFiredRef = useRef(false);
  const [generationLoading, setGenerationLoading] = useState(false);
  const isShorts = contentType === 'SHORT_FORM_CONTENT';
  const {
    short
  } = useFullShort(primaryContentId, !isShorts);
  const shortsQuizMeRequestParams = useGetShortsQuizMeRequestParams(short);
  const contentIds = useMemo(() => {
    if (isShorts && primaryContentId) {
      return [primaryContentId];
    }
    return [];
  }, [isShorts, primaryContentId]);
  const {
    getQuizMeLazy,
    quizMeError,
    quizMeLoading: pollingLoading,
    queryLoading: pollingQueryLoading,
    retryGeneration
  } = useAsyncGetLazyPollingQuizMe({
    contentIds,
    contentType
  });
  const [generateDifficultyQuizMe] = useAsyncBulkGenerateQuizMeMutation();
  const handleTriggerDifficultyGeneration = useCallback(() => {
    const alreadyFired = generationFiredRef.current;
    if (!shortsQuizMeRequestParams || isAcceptanceTest() || alreadyFired) return;
    generationFiredRef.current = true;
    setGenerationLoading(true);
    initialiseQuiz({
      contentType,
      contentId: primaryContentId
    });

    // Single call: each param is expanded into one entry per DIFFICULTY_LEVELS.
    const videoRequests = DIFFICULTY_LEVELS.flatMap(difficulty => shortsQuizMeRequestParams.map(param => Object.assign({}, param, {
      difficulty
    })));
    const requestedVideoIds = new Set(videoRequests.map(request => request.videoId));
    generateDifficultyQuizMe({
      variables: {
        videos: videoRequests
      }
    }).then(result => {
      var _result$data$result$t, _result$data;
      const tasks = (_result$data$result$t = (_result$data = result.data) === null || _result$data === void 0 ? void 0 : _result$data.result.tasks) !== null && _result$data$result$t !== void 0 ? _result$data$result$t : [];
      const allTaskIds = [];
      tasks.forEach(task => {
        const difficulty = normalizeDifficulty(task.difficulty);
        if (!difficulty || !requestedVideoIds.has(task.videoId)) return;
        setDifficultyTasks({
          contentType,
          contentId: primaryContentId,
          difficultyTasks: {
            [difficulty]: task.taskId
          }
        });
        allTaskIds.push(task.taskId);
      });
      if (allTaskIds.length > 0) {
        void getQuizMeLazy({
          variables: {
            taskIds: allTaskIds
          }
        });
      }
      if (interactiveContentTrackingProps) {
        UsageTracker.trackStandalone('Learning Center Interaction', Object.assign({}, interactiveContentTrackingProps, {
          action: 'ai-quiz-me-generated-quiz'
        }));
      }
    }).catch(err => {
      handleApolloErrors({
        error: err,
        loggingTitle: 'learningCenter.interactiveEducation.errors.asyncQuizGenerationError'
      });
    }).finally(() => {
      setGenerationLoading(false);
    });
  }, [contentType, generateDifficultyQuizMe, getQuizMeLazy, initialiseQuiz, interactiveContentTrackingProps, primaryContentId, shortsQuizMeRequestParams, setDifficultyTasks]);

  // Resets guard ref and clears all quiz state so generation re-fires on remount.
  const onAsyncRegenerateQuiz = useCallback(() => {
    generationFiredRef.current = false;
    deleteQuizState({
      contentType,
      contentId: primaryContentId
    });
    handleTriggerDifficultyGeneration();
  }, [contentType, deleteQuizState, handleTriggerDifficultyGeneration, primaryContentId]);
  const shouldTriggerQuizGeneration = triggerGeneration && !generationLoading && !hasDifficultyTasks;
  useEffect(() => {
    if ((shouldTriggerQuizGeneration || retryGeneration) && isGenerativeAiEnabled) {
      handleTriggerDifficultyGeneration();
    }
  }, [handleTriggerDifficultyGeneration, isGenerativeAiEnabled, retryGeneration, shouldTriggerQuizGeneration]);
  return {
    onAsyncRegenerateQuiz,
    generateQuizError: quizMeError,
    generateQuizLoading: generationLoading || pollingLoading || pollingQueryLoading
  };
};